import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { List as ListIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@stores/authStore';
import LanguageSelector from '../LanguageSelector';
import DarkModeToggle from '../DarkModeToggle';
import NotificationBell from './NotificationBell';
import DineoLogo from '../brand/DineoLogo';

export default function TopBar({
  onMenuToggle,
  title,
  showMenuButton = true,
  topOffset = 0,
}) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const rawRestaurantName = useAuthStore((s) => s.restaurantName);
  const restaurantName = rawRestaurantName || 'Dineo';
  const restaurantLogo = useAuthStore((s) => s.restaurantLogo);

  const isStaff = user?.accountType === 'STAFF';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
        height: { xs: 56, md: 64 },
        zIndex: theme.zIndex.appBar,
        top: topOffset,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, md: 64 },
          px: { xs: 1.5, md: 2 },
        }}
      >
        {/* Left section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showMenuButton && isMobile && (
            <IconButton
              onClick={onMenuToggle}
              edge="start"
              size="medium"
              aria-label={t('app.openNavigation', 'Navigation öffnen')}
              sx={{ color: 'text.primary' }}
            >
              <ListIcon size={24} />
            </IconButton>
          )}
          {restaurantLogo ? (
            <>
              <Avatar
                src={restaurantLogo}
                alt={restaurantName}
                sx={{ width: 28, height: 28 }}
              />
              <Tooltip title={restaurantName}>
                <Typography
                  variant="body1"
                  noWrap
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    maxWidth: isMobile ? 120 : 'none',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  {restaurantName}
                </Typography>
              </Tooltip>
            </>
          ) : rawRestaurantName ? (
            <>
              <DineoLogo variant="icon" height={24} sx={{ overflow: 'visible', flexShrink: 0 }} />
              <Tooltip title={rawRestaurantName}>
                <Typography
                  variant="body1"
                  noWrap
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    maxWidth: isMobile ? 120 : 'none',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  {rawRestaurantName}
                </Typography>
              </Tooltip>
            </>
          ) : (
            <DineoLogo variant="full" height={28} sx={{ overflow: 'visible', flexShrink: 0 }} />
          )}
        </Box>

        {/* Center: page title on mobile */}
        {isMobile && title && (
          <Typography
            variant="subtitle1"
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 600,
              maxWidth: '60%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LanguageSelector
            sx={{
              border: 'none',
              bgcolor: 'transparent',
              color: 'text.secondary',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          />
          <DarkModeToggle
            sx={{
              border: 'none',
              bgcolor: 'transparent',
              color: 'text.secondary',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          />
          {isStaff && <NotificationBell />}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
